import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Send, Phone, Users } from "lucide-react";

/**
 * CooperadosManager
 * Props:
 *   - onSend(coop): callback when "Encaminhar" is clicked (single send)
 *   - selectionMode: boolean — when true, show checkboxes and a "Encaminhar para selecionados" button
 *   - onBulkSend(coopsArray): callback for bulk send (only when selectionMode is true)
 */
export default function CooperadosManager({ onSend, selectionMode = false, onBulkSend }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("todos"); // todos | cooperados | nao_cooperados
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const load = () => api.get("/secretariat/cooperados").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = items;
    if (filter === "cooperados") arr = arr.filter((c) => c.is_cooperado);
    if (filter === "nao_cooperados") arr = arr.filter((c) => !c.is_cooperado);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((c) => c.name.toLowerCase().includes(s) || (c.phone_display || "").includes(s) || (c.community || "").toLowerCase().includes(s));
    }
    return arr;
  }, [items, q, filter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
  const someFilteredSelected = filtered.some((c) => selected.has(c.id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      // unselect all filtered
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((c) => next.delete(c.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((c) => next.add(c.id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelected(new Set());

  const triggerBulkSend = () => {
    const list = items.filter((c) => selected.has(c.id));
    if (list.length === 0) {
      toast.error("Selecione ao menos um cooperado.");
      return;
    }
    if (onBulkSend) onBulkSend(list);
  };

  const del = async (id) => {
    if (!window.confirm("Remover este cadastro?")) return;
    try { await api.delete(`/secretariat/cooperados/${id}`); toast.success("Removido."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const stats = useMemo(() => ({
    total: items.length,
    cooperados: items.filter((c) => c.is_cooperado).length,
    nao: items.filter((c) => !c.is_cooperado).length,
  }), [items]);

  return (
    <div className="mt-8" data-testid="cooperados-manager">
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[{ k: "todos", l: "Todos", v: stats.total }, { k: "cooperados", l: "Cooperados", v: stats.cooperados }, { k: "nao_cooperados", l: "Não cooperados", v: stats.nao }].map((s) => (
          <button
            key={s.k}
            type="button"
            onClick={() => setFilter(s.k)}
            data-testid={`coop-filter-${s.k}`}
            className={`text-left p-4 border transition-all ${filter === s.k ? "bg-forest-700 text-cream-100 border-forest-700" : "bg-white border-cream-400 hover:bg-cream-200"}`}
          >
            <div className={`font-display text-3xl ${filter === s.k ? "text-copaiba-300" : "text-forest-900"}`}>{s.v}</div>
            <div className={`text-xs uppercase tracking-[0.18em] mt-1 ${filter === s.k ? "text-cream-100/80" : "text-forest-900/65"}`}>{s.l}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-700/60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, telefone ou comunidade..."
            data-testid="coop-search"
            className="w-full bg-white border border-cream-400 focus:border-forest-700 focus:outline-none pl-10 pr-4 py-2.5 text-sm"
          />
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-coop-button" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
          <Plus className="w-4 h-4 mr-2" /> Cadastrar
        </Button>
      </div>

      {selectionMode && (
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-cream-200 border border-cream-400 px-4 py-3" data-testid="bulk-toolbar">
          <div className="inline-flex items-center gap-2 text-sm text-forest-900">
            <Checkbox
              checked={allFilteredSelected}
              onCheckedChange={toggleAll}
              data-testid="coop-select-all"
              className="data-[state=checked]:bg-forest-700 data-[state=checked]:border-forest-700"
            />
            <button
              type="button"
              onClick={toggleAll}
              data-testid="coop-select-all-label"
              className="hover:text-copaiba-700"
            >
              {allFilteredSelected ? "Desmarcar todos" : "Marcar todos"}
            </button>
            <span className="text-xs text-forest-900/65">({filtered.length} listados)</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-forest-900/75" data-testid="bulk-selected-count">
              <strong>{selected.size}</strong> selecionado(s)
            </span>
            {selected.size > 0 && (
              <button type="button" onClick={clearSelection} data-testid="coop-clear-selection" className="text-xs text-forest-700 hover:text-copaiba-700 underline">
                Limpar
              </button>
            )}
            <Button
              onClick={triggerBulkSend}
              disabled={selected.size === 0}
              data-testid="bulk-send-button"
              className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none disabled:opacity-50"
            >
              <Users className="w-4 h-4 mr-2" /> Encaminhar para selecionados
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-cream-400 overflow-hidden">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`coop-row-${c.id}`} className="flex flex-col md:flex-row md:items-center gap-3 px-5 py-4 border-b last:border-0 border-cream-400">
            {selectionMode && (
              <div className="shrink-0">
                <Checkbox
                  checked={selected.has(c.id)}
                  onCheckedChange={() => toggleOne(c.id)}
                  data-testid={`coop-check-${c.id}`}
                  className="data-[state=checked]:bg-forest-700 data-[state=checked]:border-forest-700"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-forest-900">{c.name}</div>
                {c.is_cooperado ? (
                  <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-forest-700 text-cream-100">Cooperado(a)</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-copaiba-500 text-forest-900">Não cooperado(a)</span>
                )}
              </div>
              <div className="text-sm text-forest-900/70 mt-1 flex items-center gap-3 flex-wrap">
                <a href={`https://wa.me/${c.phone}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-copaiba-700" data-testid={`coop-whatsapp-${c.id}`}>
                  <Phone className="w-3.5 h-3.5" /> {c.phone_display}
                </a>
                {c.community && <span>· {c.community}</span>}
                {c.email && <span>· {c.email}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onSend && !selectionMode && (
                <button onClick={() => onSend(c)} data-testid={`coop-send-${c.id}`} className="text-xs px-3 py-1.5 bg-copaiba-500 text-forest-900 hover:bg-copaiba-300 inline-flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5" /> Encaminhar
                </button>
              )}
              <button onClick={() => { setEditing(c); setOpen(true); }} data-testid={`coop-edit-${c.id}`} className="p-2 text-forest-700 hover:bg-cream-200"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(c.id)} data-testid={`coop-delete-${c.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="px-5 py-10 text-center text-forest-900/65">Nenhum resultado.</div>
        )}
      </div>

      <CoopDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function CoopDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", is_cooperado: true, community: "", email: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({
      name: editing.name, phone: editing.phone_display || editing.phone,
      is_cooperado: editing.is_cooperado, community: editing.community || "",
      email: editing.email || "", notes: editing.notes || "",
    });
    else setForm({ name: "", phone: "", is_cooperado: true, community: "", email: "", notes: "" });
  }, [editing, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.put(`/secretariat/cooperados/${editing.id}`, form); toast.success("Atualizado."); }
      else { await api.post("/secretariat/cooperados", form); toast.success("Cadastrado!"); }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Editar cadastro" : "Novo cadastro"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Nome completo</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="coop-form-name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required placeholder="(93) 99162-1644" data-testid="coop-form-phone" /></div>
            <div><Label>Comunidade</Label><Input value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })} data-testid="coop-form-community" /></div>
          </div>
          <div><Label>E-mail (opcional)</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="coop-form-email" /></div>
          <div><Label>Observações (opcional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="coop-form-notes" /></div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_cooperado} onCheckedChange={(v) => setForm({ ...form, is_cooperado: v })} data-testid="coop-form-iscoop" />
            <Label>É cooperado(a) da COOMFLONA</Label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="coop-form-submit" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
